import cn from 'classnames';
import React, { memo, useMemo } from 'react';

import DetailsTabRow from '@sravni/vitrina/lib/enums/config/DetailsTabRow';
import getProperty from '@sravni/vitrina/lib/helpers/getProperty';
import { getVariantHelper } from '@sravni/vitrina/lib/helpers/getVariantHelper';
import type IProduct from '@sravni/vitrina/lib/types/IProduct';
import { Tooltip, Wysiwyg } from '@sravni/vitrina-components';

import { vitrinsFilters } from '@components/RatingTable/components/ProductPopup/constants/filters';

import { useDictionaries, useProductConfig } from '../../../../../../store/hooks/productPopupInfo';
import type ITabDetail from '../../interfaces/ITabDetail';
import { Content } from '../ui/Table/Content';
import { Section } from '../ui/Table/Section';
import { Table } from '../ui/Table/Table';
import { Title } from '../ui/Table/Title';

import { useRows } from './hooks';
import style from './ProductTable.module.scss';

interface IProps {
  className?: string;
  additionalInformationClassName?: string;
  config?: ITabDetail;
  product: IProduct;
  isProductPage?: boolean;
}

const DEFAULT_COLUMN_COUNT = 12;

const filters = vitrinsFilters;

const ProductTable: React.FC<IProps> = memo((props: IProps) => {
  const { className, config, product, additionalInformationClassName, isProductPage } = props;
  const vitrinsConfig = useProductConfig();
  const dictionaries = useDictionaries();
  const rows = useRows(product, config);
  const spans = config?.table?.options?.spans;

  const tableVariables = useMemo(
    () =>
      ({
        '--sum-spans': spans ? spans.reduce((sum, span) => sum + span) : DEFAULT_COLUMN_COUNT,
        '--column-count': config.table.options.titles.length,
      } as React.CSSProperties),
    [config, spans],
  );

  return (
    <>
      <div className={cn(className, style.wrapper)}>
        <Table className={style.table} hasBorder={isProductPage} style={tableVariables}>
          <thead>
          <Section inversion>
            {config.table.options.titles.map((title, index) => {
              if (typeof title === 'string') {
                return (
                  <Title className={cn(spans ? style[`span-${spans[index]}`] : style['span-default'])} key={title}>
                    <span>{title}</span>
                  </Title>
                );
              }

              const tooltip =
                'variants' in title.tooltip
                  ? getVariantHelper(title.tooltip.variants, filters, product)
                  : title.tooltip;

              return (
                <Title
                  className={cn(spans ? style[`span-${spans[index]}`] : style['span-default'])}
                  key={title.title}
                >
                  <div className={style['title-inner']}>
                    <span>{title.title}</span> <Tooltip product={product} tooltipData={tooltip} />
                  </div>
                </Title>
              );
            })}
          </Section>
          </thead>
          <tbody>
          {rows.map((row, index) => {
            const sectionKey = `${config.table.tableName}-${index}`;

            return (
              <Section key={sectionKey}>
                {config.table.options.contents.map((content, contentIndex: number) => {
                  const contentValue =
                    'variants' in content ? getVariantHelper(content.variants, filters, product, row) : content;

                  const contentKey = `${config.table.tableName}-${
                    'property' in contentValue ? contentValue.property : ''
                  }-${contentIndex}`;

                  const text = getProperty(vitrinsConfig, contentValue, row, dictionaries);

                  return (
                    <Content
                      key={contentKey}
                      className={spans ? style[`span-${spans[contentIndex]}`] : style['span-default']}
                    >
                      {contentValue.type === DetailsTabRow.Wysiwyg ||
                      contentValue.type === DetailsTabRow.TemplateWysiwyg ? (
                        <Wysiwyg content={text as string} />
                      ) : (
                        text
                      )}
                      {contentValue.tooltip && (
                        <span className={style.tooltip}>
                            <Tooltip product={row} tooltipData={contentValue.tooltip} />
                          </span>
                      )}
                    </Content>
                  );
                })}
              </Section>
            );
          })}
          </tbody>
        </Table>
      </div>

      {config.additionalInformation && product[config.additionalInformation.property] && (
        <div className={additionalInformationClassName}>
          <Wysiwyg content={product[config.additionalInformation.property]} />
        </div>
      )}
    </>
  );
});

export default ProductTable;
